import { AppHeader } from "@/components/app-header";
import { AppFooter } from "@/components/app-footer";
import { LocaleLink } from "@/components/locale-link";
import { useTranslation } from "@/common/providers/i18n-provider";
import { CONTACT_URL, SIGN_UP_LEDGER } from "@/config/product-links";
import {
  FEATURED_FOUNDER_RESOURCES,
  FOUNDER_RESOURCE_CATALOG,
  FOUNDER_RESOURCES,
  founderResourcePath,
  type FounderResource,
  type FounderResourceType,
} from "@/config/founder-resources";
import { productUrl } from "@/lib/product-attribution";
import { BookOpen, Calculator, FileText, ArrowUpRight } from "lucide-react";
import "@/styles/company-pages.css";
import "@/styles/founder-resources.css";

const RESOURCE_TYPES = {
  guide: { labelKey: "founder.resources.type.guide", Icon: BookOpen },
  template: { labelKey: "founder.resources.type.template", Icon: FileText },
  calculator: {
    labelKey: "founder.resources.type.calculator",
    Icon: Calculator,
  },
  generator: { labelKey: "founder.resources.type.generator", Icon: FileText },
  article: { labelKey: "founder.resources.type.article", Icon: BookOpen },
} satisfies Record<
  FounderResourceType,
  { labelKey: string; Icon: typeof BookOpen }
>;

const BROWSE_CATEGORIES = FOUNDER_RESOURCE_CATALOG.map((category) => ({
  ...category,
  resources: FOUNDER_RESOURCES.filter(
    (resource) => resource.categoryId === category.id && !resource.featured,
  ),
}));

function FounderResourceLink({ resource }: { resource: FounderResource }) {
  const { t } = useTranslation();
  const { labelKey, Icon } = RESOURCE_TYPES[resource.resourceType];
  const Heading = resource.featured ? "h3" : "h4";
  return (
    <LocaleLink
      to={resource.path}
      className={
        resource.featured ? "founder-tool-card" : "founder-resource-link"
      }
    >
      <div className="founder-resource-type">
        {resource.featured ? <Icon size={27} aria-hidden="true" /> : null}
        <span>{t(labelKey)}</span>
      </div>
      <Heading>{t(resource.titleKey)}</Heading>
      <p>{t(resource.descriptionKey)}</p>
      {resource.featured ? (
        <ArrowUpRight
          size={22}
          className="founder-tool-arrow"
          aria-hidden="true"
        />
      ) : null}
    </LocaleLink>
  );
}

export function FounderResourcesIndexComponent() {
  const { t } = useTranslation();
  return (
    <div className="company-page founder-page">
      <AppHeader />
      <main id="main">
        <section
          className="company-container founder-hub-hero"
          aria-labelledby="founder-resources-heading"
        >
          <h1 id="founder-resources-heading">
            {t("homepage.founderResources")}
          </h1>
          <div>
            <p className="company-lead">
              {t("founder.resources.hero.description")}
            </p>
            <div className="company-actions">
              <a href="#founder-tools" className="company-button">
                {t("founder.resources.tools.eyebrow")}
              </a>
              <a href="#founder-browse" className="company-text-link">
                {t("founder.resources.browse.title")}
              </a>
            </div>
          </div>
        </section>
        <section
          className="founder-tools"
          id="founder-tools"
          aria-labelledby="founder-resources-tools-heading"
        >
          <div className="company-container">
            <div className="founder-section-heading">
              <h2 id="founder-resources-tools-heading">
                {t("founder.resources.tools.title")}
              </h2>
              <p>{t("founder.resources.tools.description")}</p>
            </div>
            <div className="founder-tool-grid">
              {FEATURED_FOUNDER_RESOURCES.map((resource) => (
                <FounderResourceLink key={resource.id} resource={resource} />
              ))}
            </div>
          </div>
        </section>
        <section
          className="company-container founder-browse"
          id="founder-browse"
          aria-labelledby="founder-resources-browse-heading"
        >
          <div className="founder-topic-index">
            <h2 id="founder-resources-browse-heading">
              {t("founder.resources.browse.title")}
            </h2>
            <p>{t("founder.resources.browse.description")}</p>
            <nav aria-labelledby="founder-resources-category-nav-label">
              <h3 id="founder-resources-category-nav-label">
                {t("founder.resources.category-nav.label")}
              </h3>
              <ul>
                {BROWSE_CATEGORIES.map((category) => (
                  <li key={category.id}>
                    <a href={`#founder-resources-category-${category.id}`}>
                      {t(category.titleKey)}
                    </a>
                  </li>
                ))}
              </ul>
            </nav>
          </div>
          <div className="founder-topics">
            {BROWSE_CATEGORIES.map((category) => (
              <section
                key={category.id}
                id={"founder-resources-category-" + category.id}
                aria-labelledby={"founder-topic-" + category.id}
              >
                <h3 id={"founder-topic-" + category.id}>
                  {t(category.titleKey)}
                </h3>
                <ul>
                  {category.resources.map((resource) => (
                    <li key={resource.id}>
                      <FounderResourceLink resource={resource} />
                    </li>
                  ))}
                </ul>
              </section>
            ))}
          </div>
        </section>
        <section
          className="company-container founder-next"
          aria-labelledby="founder-resources-next-step-heading"
        >
          <div>
            <h2 id="founder-resources-next-step-heading">
              {t("founder.resources.closing.title")}
            </h2>
            <p>{t("founder.resources.closing.description")}</p>
          </div>
          <div className="company-actions">
            <a
              href={productUrl(SIGN_UP_LEDGER, "founder_resources")}
              className="company-button"
            >
              {t("founder.resources.closing.start-free")}
            </a>
            <LocaleLink
              to={founderResourcePath("beancount-setup")}
              className="company-text-link"
            >
              {t("founder.resources.closing.read-guide")}
            </LocaleLink>
            <a
              href={productUrl(CONTACT_URL, "founder_resources")}
              className="company-text-link"
            >
              {t("founder.resources.closing.join-community")}
            </a>
          </div>
        </section>
      </main>
      <AppFooter />
    </div>
  );
}
